import { Typography, Box, Chip, Divider } from '@mui/material';
import NoHealthRecordDataCard, { HealthRecordCategory } from 'components/NoHealthRecordDataCard';
import PatientDetailsCard from 'components/PatientDetailsCard';
import UserAddedToolTip from 'pages/Dashboard/components/UserAddedToolTip';
import { ShowMoreDrawerChildProps, sliceForShowMore } from 'pages/Dashboard/ShowMoreDrawer';
import { Allergy } from 'pages/Dashboard/types/allergies.types';
import * as React from 'react';
import theme, { tertiary } from 'theme';
import { googleTimestampToDateString } from 'utils/dateUtils';


interface AllergiesCardProps extends ShowMoreDrawerChildProps{
  gridSize?: number;
  allergies?: Allergy[];
  isNewUi?: boolean;
}

interface AllergyDetaisProps {
  allergies: Allergy[];
}

function AlleryDetails({ allergies }: AllergyDetaisProps) : JSX.Element {
  const datedAllergies: Allergy[] = [];
  const nonDatedAllergies: Allergy[] = [];
  for (const allergy of allergies) {
    if (allergy.started) {
      datedAllergies.push(allergy);
    }
    else {
      nonDatedAllergies.push(allergy);
    }
  }

  return (
    <Box display='flex' flexDirection='column' width='100%'>
      {datedAllergies.map((currentAllergy, idx) => (
        <>
          <Box
            key={`health-history-${currentAllergy.id}`}
            display='flex'
            flexDirection='row'
            alignItems='center'
            width='100%'
            py={1.5}
          >
            <Box display='flex' flexDirection='column' width='100%'>
              <Typography variant='body2' color='text.secondary'>
                {googleTimestampToDateString(currentAllergy.started)}
              </Typography>
              <Typography variant='body1' color='text.primary'>
                {currentAllergy.substance}
              </Typography>
              <Box display='flex' flexWrap='wrap' gap={1} mt={1}>
                {currentAllergy.reactions?.map((r, i) => (
                  <Chip
                    key={r.id}
                    label={r.name}
                    size='small'
                    sx={{
                      background: tertiary.light,
                      color: tertiary.dark,
                    }}
                  />
                ))}
              </Box>
            </Box>
            <UserAddedToolTip source={currentAllergy.source} />
          </Box>
          {idx < datedAllergies.length - 1 && (
            <Divider sx={{ borderColor: theme.custom.colors.borderColor, width: '100%' }} />
          )}
        </>
      ))}
      {nonDatedAllergies.length ? (
        <Box display='flex' flexDirection='column' width='100%'>
          <Typography mb={2} variant='body1' color='text.secondary'>
            Unknown dates
          </Typography>
          {nonDatedAllergies.map((currentAllergy, idx) => (
            <>
              <Box
                key={`health-history-${currentAllergy.id}`}
                display='flex'
                flexDirection='row'
                alignItems='center'
                width='100%'
                py={1.5}
              >
                <Box display='flex' flexDirection='column' width='100%'>
                  <Typography variant='body1' color='text.primary'>
                    {currentAllergy.substance}
                  </Typography>
                  <Box display='flex' flexWrap='wrap' gap={1} mt={1}>
                    {currentAllergy.reactions?.map((r, i) => (
                      <Chip
                        key={r.id}
                        label={r.name}
                        size='small'
                        sx={{
                          background: tertiary.light,
                          color: tertiary.dark,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <UserAddedToolTip source={currentAllergy.source} />
              </Box>
              {idx < nonDatedAllergies.length - 1 && (
                <Divider sx={{ borderColor: theme.custom.colors.borderColor, width: '100%' }} />
              )}
            </>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export default function AllergiesCard({
  gridSize,
  drawerMode = false,
  allergies = [],
  onShowMore,
  isNewUi = false,
}: AllergiesCardProps): JSX.Element | null {
  const datedAllergies = allergies.filter((allergy) => allergy.started);
  const nonDatedAllergies = allergies.filter((allergy) => !allergy.started)
    .sort((a, b) => (a.substance).localeCompare(b.substance));

  const sortedAllergies = [...datedAllergies, ...nonDatedAllergies];
  const data = sliceForShowMore(sortedAllergies, !drawerMode);

  return (
    <PatientDetailsCard
      title='Allergies'
      showHeader={!drawerMode && allergies?.length > 0}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      viewDetailsBtnLabel='View all'
      showViewDetails={!drawerMode && data.length > 3 && !!onShowMore}
      onViewDetails={onShowMore as VoidFunction}
      key='health-history-allergies'
      showSubHeader={false}
    >
      {allergies?.length === 0 && isNewUi ? (
        <NoHealthRecordDataCard
          title='No allergies found'
          description='Data will be available here once there is a activity to record'
          healthRecordCategory={HealthRecordCategory.allergies}
        />
      ) : (
        <AlleryDetails allergies={data} />
      )}
    </PatientDetailsCard>
  );
}
